import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  listUsers,
  createUser,
  createUsersBulk,
  updateUser,
  deleteUser,
  resendInvite,
  getUser,
  regeneratePin,
} from '../../../api/adminUsersApi';
import { showSuccess, showError, showWarning } from '../../../utils/notifications';
import { getContextualError, logError } from '../../../utils/errorHandling';
import { parseCsv, readFileAsText } from '../../../utils/csvParsing';
import SearchFilter from '../../common/SearchFilter';
import CollapsibleSection from '../../common/CollapsibleSection';
import CsvFormatExplainer from '../CsvFormatExplainer';
import { useLoadingStates } from '../../../hooks/useLoadingStates';
import type { components } from '../../../api/types.gen';
import styles from './UserManagementPanel.module.css';
import adminStyles from '../AdminPanel.module.css';

type UserResponse = components['schemas']['UserResponse'];
type CreateUserBody = components['schemas']['CreateUserBody'];
type BulkUserResult = components['schemas']['BulkUserResult'];
// Backend Role enum (STUDENT|TEACHER|ADMIN|SUPER_ADMIN) as accepted by the
// create/update bodies — narrower than UserResponse['role'], which also
// includes SERVICE.
type Role = NonNullable<CreateUserBody['role']>;

// Result of validateCsvFormat — rowCount is absent on the two early-return
// (invalid content) branches.
interface CsvValidationResult {
  errors: string[];
  warnings: string[];
  rowCount?: number;
}

// Metadata rendered for a selected CSV file after analyzeCsvFile validates it.
interface CsvFileAnalysis {
  accountCount: number;
  isLargeFile: boolean;
  fileSize: string;
  formatValid: boolean;
}

interface UserManagementPanelProps {
  schoolId?: number | string | null;
}

// Ported from AdminPanel.js's "User Account Management" section
// (JSX lines 3345-4300) as part of the Firebase -> hallpass-backend
// migration. Google Auth accounts, the Blocked Users tab, and the
// approved-emails allowlist are all out of scope now that auth is
// email/password only (confirmed scope decision) — this panel is
// email/password accounts only.

const PAGE_SIZE = 20;

// Backend Role enum values (packages/types/src/schemas.ts's
// ASSIGNABLE_ROLES) — the REST API takes/returns these uppercase strings
// directly, unlike AuthContext's lowercase UI-facing mapping.
const ROLE_LABELS: Partial<Record<UserResponse['role'], string>> = {
  STUDENT: 'Student',
  TEACHER: 'Teacher',
  ADMIN: 'Admin',
  SUPER_ADMIN: 'Super Admin',
};

// Client-side name validation (mirrors backend validation) — ported as-is
// from AdminPanel.js's validateNameField (lines 2165-2219); unrelated to
// the Firebase -> REST swap.
const validateNameField = (name: string): string | null => {
  if (!name || typeof name !== 'string') return 'Name is required';

  const trimmed = name.trim();
  if (trimmed.length < 2) return 'Name must be at least 2 characters long';
  if (trimmed.length > 50)
    return 'Name must be no more than 50 characters long';
  if (!/[a-zA-Z]/.test(trimmed)) return 'Name must contain at least one letter';

  const blockedValues = [
    'unknown',
    'n/a',
    'na',
    'tbd',
    'to be determined',
    '?',
    '???',
    'test',
    'temp',
    'temporary',
    'placeholder',
    'none',
    'null',
    'undefined',
    'xxx',
    'zzz',
    'admin',
    'user',
    'student',
    'name',
    'first last',
    'firstname lastname',
    'john doe',
    'jane doe',
  ];

  const lowerName = trimmed.toLowerCase();
  if (blockedValues.includes(lowerName)) {
    return `Name cannot be a placeholder value like "${trimmed}". Please provide a real name.`;
  }

  if (/^[0-9\s\-_.,!@#$%^&*()+={}[\]|\\:";'<>?/~`]+$/.test(trimmed)) {
    return 'Name cannot consist only of numbers or symbols';
  }

  if (/^(.)\1+$/.test(trimmed) && trimmed.length <= 4) {
    return `Name cannot be repeated characters like "${trimmed}"`;
  }

  return null;
};

// Ported from AdminPanel.js's validateCsvFormat (lines 2221-2317).
const validateCsvFormat = (csvContent: string): CsvValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!csvContent || typeof csvContent !== 'string') {
    errors.push(
      "Invalid file content. Please ensure you're uploading a text CSV file."
    );
    return { errors, warnings };
  }

  const { headers, dataRows } = parseCsv(csvContent);

  if (dataRows.length === 0) {
    errors.push(
      'File must contain at least a header row and one data row.'
    );
    return { errors, warnings, rowCount: 0 };
  }

  const requiredHeaders = ['email', 'name', 'role'];
  const missingHeaders = requiredHeaders.filter(
    (req) => !headers.includes(req)
  );

  if (missingHeaders.length > 0) {
    errors.push(
      `Missing required column(s): ${missingHeaders.join(', ')}. Header should be: email,name,role`
    );
  }

  const emailIndex = headers.indexOf('email');
  const nameIndex = headers.indexOf('name');
  const roleIndex = headers.indexOf('role');
  const seenEmails = new Map<string, number>();

  for (let i = 0; i < dataRows.length; i++) {
    const values = dataRows[i];
    const rowNum = i + 2;

    if (values.length !== headers.length) {
      errors.push(
        `Row ${rowNum}: Expected ${headers.length} columns but found ${values.length}. Check for missing commas or extra commas.`
      );
    }

    if (
      emailIndex >= 0 &&
      values[emailIndex] &&
      !values[emailIndex].includes('@')
    ) {
      errors.push(
        `Row ${rowNum}: "${values[emailIndex]}" doesn't look like a valid email address.`
      );
    }

    if (emailIndex >= 0 && values[emailIndex]) {
      const normalizedEmail = values[emailIndex].toLowerCase();
      if (seenEmails.has(normalizedEmail)) {
        errors.push(
          `Row ${rowNum}: duplicate email "${values[emailIndex]}" (also used in row ${seenEmails.get(normalizedEmail)}).`
        );
      } else {
        seenEmails.set(normalizedEmail, rowNum);
      }
    }

    if (nameIndex >= 0 && values[nameIndex]) {
      const nameError = validateNameField(values[nameIndex]);
      if (nameError) {
        errors.push(`Row ${rowNum}: ${nameError}`);
      }
    } else if (nameIndex >= 0) {
      errors.push(`Row ${rowNum}: Name field is empty or missing.`);
    }

    if (roleIndex >= 0 && values[roleIndex]) {
      const role = values[roleIndex].toLowerCase();
      if (!['student', 'teacher', 'admin'].includes(role)) {
        errors.push(
          `Row ${rowNum}: "${values[roleIndex]}" is not a valid role. Must be: student, teacher, or admin.`
        );
      }
    } else if (roleIndex >= 0) {
      errors.push(`Row ${rowNum}: Role field is empty or missing.`);
    }
  }

  if (dataRows.length > 100) {
    warnings.push(
      `Large file detected (${dataRows.length} accounts). Processing may take a few minutes.`
    );
  }

  return { errors, warnings, rowCount: dataRows.length };
};

// Combines validateCsvFormat's errors/warnings into the single message
// string shown via showError. Shared by analyzeCsvFile (file-upload path)
// and handleCsvUpload's paste-validation branch.
const formatCsvValidationError = (validation: CsvValidationResult): string => {
  const errorMessage = `CSV Format Issues Found:\n\n${validation.errors.join('\n')}\n\nPlease fix these issues and try again.`;
  const warningMessage =
    validation.warnings.length > 0
      ? `\n\nWarnings:\n${validation.warnings.join('\n')}`
      : '';
  return errorMessage + warningMessage;
};

// Parses a validated email,name,role CSV into the array shape
// createUsersBulk expects — role is upper-cased to match the backend's
// Role enum (STUDENT/TEACHER/ADMIN), which the CSV's lowercase convention
// doesn't use directly.
const parseCsvToUsers = (csvContent: string): CreateUserBody[] => {
  const { headers, dataRows } = parseCsv(csvContent);
  const emailIndex = headers.indexOf('email');
  const nameIndex = headers.indexOf('name');
  const roleIndex = headers.indexOf('role');

  return dataRows.map((values) => {
    const user: CreateUserBody = {
      email: values[emailIndex],
      name: values[nameIndex],
    };
    const role = roleIndex >= 0 ? values[roleIndex] : '';
    if (role) {
      // Validation upstream (validateCsvFormat) guarantees role is one of
      // student/teacher/admin, so the upper-cased value is a valid Role.
      user.role = role.toUpperCase() as Role;
    }
    return user;
  });
};

const UserManagementPanel = ({
  schoolId: schoolIdProp,
}: UserManagementPanelProps = {}): React.JSX.Element => {
  const { currentUser, userRole } = useAuth();
  // currentUser is the mapped /api/users/me response set by AuthContext
  // (src/context/AuthContext.js) — schoolId here is a fallback for callers
  // that don't pass one explicitly (e.g. a future developer-mode org
  // override, mirroring AdminPanel.js's existing developerModeOrgId
  // pattern).
  const schoolId = schoolIdProp ?? currentUser?.schoolId;
  const isSuperAdmin = userRole === 'developer'; // AuthContext's ROLE_MAP: SUPER_ADMIN -> 'developer'

  // Only a SUPER_ADMIN caller may set schoolId on a create body — for any
  // other caller (ADMIN), the backend 403s on a body that even contains the
  // key, so it must be omitted entirely rather than sent as undefined.
  const withSchoolId = useCallback(
    <T extends object>(body: T): T =>
      isSuperAdmin && schoolId ? { ...body, schoolId: Number(schoolId) } : body,
    [isSuperAdmin, schoolId]
  );

  const [loadingStates, setLoading] = useLoadingStates({
    loadUsers: false,
    userRole: false,
    createUser: false,
    createUsersBulk: false,
    deleteUser: false,
    resendInvite: false,
  });

  // studentId -> the student's PIN, populated on demand by "View PIN" /
  // "Regenerate PIN". Only students have a pinCode (the parent voice-lookup
  // tool), so these actions are offered on STUDENT rows only. A value of
  // null (student has no pin yet) renders as "None".
  const [pinByUserId, setPinByUserId] = useState<
    Record<number, string | null>
  >({});

  // studentId -> { view?: bool, regenerate?: bool } — per-row loading flags
  // for the View PIN / Regenerate PIN actions. Kept per-user (like
  // pinByUserId) so acting on one student's row only disables that row's
  // buttons, not the same action on every other student row.
  const [pinLoadingByUserId, setPinLoadingByUserId] = useState<
    Record<number, { view?: boolean; regenerate?: boolean }>
  >({});
  const setPinLoading = useCallback(
    (userId: number, key: 'view' | 'regenerate', value: boolean) => {
      setPinLoadingByUserId((prev) => ({
        ...prev,
        [userId]: { ...prev[userId], [key]: value },
      }));
    },
    []
  );

  // Existing accounts list — server-side paginated (the users API is
  // cursor-based, not offset/total-based, so there's no "total items"
  // available to page-number pagination; see the custom pager rendered
  // below instead of common/PaginationControls, whose props assume a known
  // totalPages/totalItems).
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [usersFilter, setUsersFilter] = useState('');
  // cursorHistory[p] is the `cursor` query param used to fetch page p;
  // cursorHistory[0] is always undefined (first page has no cursor). A new
  // entry is appended only once a fetch reveals that page exists.
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([
    undefined,
  ]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  const fetchUsers = useCallback(
    async (
      page: number,
      cursors: (string | undefined)[],
      filterValue: string,
      isCancelled: () => boolean = () => false
    ) => {
      if (!isCancelled()) setLoading('loadUsers', true);
      try {
        const result = await listUsers({
          q: filterValue,
          cursor: cursors[page],
          limit: PAGE_SIZE,
        });
        const data =
          isSuperAdmin && schoolId
            ? result.data.filter((u) => Number(u.schoolId) === Number(schoolId))
            : result.data;
        if (!isCancelled()) {
          setUsers(data);
          setHasNextPage(Boolean(result.nextCursor));
          // Rebuild from the cursors this fetch was actually issued with
          // (not whatever cursorHistory happens to hold by the time this
          // resolves), truncated to this page and extended by one if
          // another page exists.
          const nextCursors = cursors.slice(0, page + 1);
          if (result.nextCursor) {
            nextCursors[page + 1] = result.nextCursor;
          }
          setCursorHistory(nextCursors);
          setCurrentPage(page);
        }
      } catch (error) {
        logError(error, 'loadUsers');
        if (!isCancelled()) {
          showError(
            getContextualError(
              error,
              undefined,
              'Failed to load users. Please try again.'
            )
          );
          setUsers([]);
        }
      } finally {
        if (!isCancelled()) setLoading('loadUsers', false);
      }
    },
    [setLoading, isSuperAdmin, schoolId]
  );

  // Initial load, and reload from page 0 whenever the search filter
  // changes. SearchFilter already debounces onChange, but that only
  // narrows the window where two distinct search terms can both be
  // in flight at once — it doesn't close it, so a stale response for an
  // earlier search term could still resolve after a later one and clobber
  // `users` with results for the wrong term. The cancelled flag closes
  // that gap, mirroring useAdminSetupChecklist.js's fetchCounts.
  //
  // `fetchUsers` is a dep (not eslint-disabled) so that a SUPER_ADMIN
  // switching orgs via developerModeOrgId — which flows in as the
  // `schoolId` prop and thus changes fetchUsers's identity — refetches
  // from page 0 with the new org's cursor/filter, instead of stranding
  // the panel on the previous school's list.
  useEffect(() => {
    let cancelled = false;
    fetchUsers(0, [undefined], usersFilter, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [usersFilter, fetchUsers]);

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 0 || nextPage >= cursorHistory.length) return;
    fetchUsers(nextPage, cursorHistory, usersFilter);
  };

  const refreshCurrentPage = () =>
    fetchUsers(currentPage, cursorHistory, usersFilter);

  // Role update — replaces the 'updateUserRole' httpsCallable. better-auth
  // session roles are authoritative now, so there's no client-side custom
  // claims/token-refresh step to run after this.
  const handleRoleChange = async (user: UserResponse, newRole: Role) => {
    setLoading('userRole', true);
    try {
      await updateUser(user.id, { role: newRole });
      showSuccess(`Updated ${user.email}'s role to ${ROLE_LABELS[newRole]}.`);
      await refreshCurrentPage();
    } catch (error) {
      logError(error, 'updateUserRole');
      showError(
        getContextualError(
          error,
          undefined,
          'Failed to update user role. Please try again.'
        )
      );
    } finally {
      setLoading('userRole', false);
    }
  };

  // Single-user creation — replaces the 'createUserAccounts' action:'create'
  // httpsCallable. There is no password field anymore: createUser sends a
  // set-password invite email server-side instead of provisioning a
  // temporary password.
  const [singleUserEmail, setSingleUserEmail] = useState('');
  const [singleUserName, setSingleUserName] = useState('');
  const [singleUserRole, setSingleUserRole] = useState<Role>('STUDENT');

  const createSingleUserAccount = async () => {
    if (!singleUserEmail || !singleUserName) {
      showError('Please fill in all required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(singleUserEmail)) {
      showError('Please enter a valid email address');
      return;
    }

    // Same placeholder-name/length/letter rules the CSV path enforces via
    // validateNameField — without this a bad name only surfaces as a raw
    // server error instead of a friendly client message.
    const nameError = validateNameField(singleUserName);
    if (nameError) {
      showError(nameError);
      return;
    }

    setLoading('createUser', true);
    try {
      await createUser(
        withSchoolId({
          email: singleUserEmail,
          name: singleUserName,
          role: singleUserRole,
        })
      );
      showSuccess(
        `Account created for ${singleUserEmail}. An invite email has been sent.`
      );
      setSingleUserEmail('');
      setSingleUserName('');
      setSingleUserRole('STUDENT');
      await fetchUsers(0, [undefined], usersFilter);
    } catch (error) {
      logError(error, 'createSingleUserAccount');
      showError(
        getContextualError(
          error,
          undefined,
          'Failed to create account. Please try again.'
        )
      );
    } finally {
      setLoading('createUser', false);
    }
  };

  // CSV bulk creation — replaces handleCsvUpload/handleBulkCsvUpload's
  // 500-account manual batching (that was working around Cloud Function
  // deadlines). createUsersBulk already chunks at 100/batch and re-indexes
  // failures back to the original array offset, so this just parses the
  // CSV and calls it once.
  const [userCsvFile, setUserCsvFile] = useState<File | null>(null);
  const [csvFileAnalysis, setCsvFileAnalysis] =
    useState<CsvFileAnalysis | null>(null);
  const [csvContentCache, setCsvContentCache] = useState<string | null>(null);
  const [csvUploadResults, setCsvUploadResults] =
    useState<BulkUserResult | null>(null);
  // Alternative to the file upload above — mutually exclusive with it (each
  // input is disabled while the other has content) so handleCsvUpload always
  // has a single unambiguous source.
  const [pastedCsvText, setPastedCsvText] = useState('');
  const [pendingAccountCount, setPendingAccountCount] = useState<number | null>(
    null
  );

  const analyzeCsvFile = async (file: File | null) => {
    if (!file) {
      setCsvFileAnalysis(null);
      setCsvContentCache(null);
      return;
    }

    try {
      const csvContent = await readFileAsText(file);

      const validation = validateCsvFormat(csvContent);

      if (validation.errors.length > 0) {
        showError(formatCsvValidationError(validation));
        setCsvFileAnalysis(null);
        setCsvContentCache(null);
        const fileInput = document.getElementById('user-csv-file-upload');
        if (fileInput instanceof HTMLInputElement) {
          fileInput.value = '';
        }
        setUserCsvFile(null);
        return;
      }

      setCsvContentCache(csvContent);

      const accountCount = validation.rowCount ?? 0;
      const isLargeFile = accountCount > 100;

      setCsvFileAnalysis({
        accountCount,
        isLargeFile,
        fileSize: (file.size / 1024).toFixed(1),
        formatValid: true,
      });
    } catch (error) {
      console.error('Error analyzing CSV file:', error);
      showError(error instanceof Error ? error.message : String(error));
      setCsvFileAnalysis(null);
      setCsvContentCache(null);
      const fileInput = document.getElementById('user-csv-file-upload');
      if (fileInput instanceof HTMLInputElement) {
        fileInput.value = '';
      }
      setUserCsvFile(null);
    }
  };

  const handleCsvFileSelection = (file: File | null) => {
    setUserCsvFile(file);
    setPastedCsvText('');
    analyzeCsvFile(file);
  };

  const handleCsvUpload = async () => {
    let csvContent: string;
    let accountCount: number | null;

    if (userCsvFile) {
      if (!csvContentCache) {
        showError('Please select a valid CSV file first');
        return;
      }
      csvContent = csvContentCache;
      accountCount = csvFileAnalysis?.accountCount ?? null;
    } else {
      if (!pastedCsvText.trim()) {
        showError('Please select a CSV file or paste CSV data first');
        return;
      }
      const validation = validateCsvFormat(pastedCsvText);
      if (validation.errors.length > 0) {
        showError(formatCsvValidationError(validation));
        return;
      }
      if (validation.warnings.length > 0) {
        showWarning(validation.warnings.join('\n'));
      }
      csvContent = pastedCsvText;
      accountCount = validation.rowCount ?? null;
    }

    setPendingAccountCount(accountCount);
    setLoading('createUsersBulk', true);
    setCsvUploadResults(null);

    try {
      const usersToCreate = parseCsvToUsers(csvContent).map(withSchoolId);
      const result = await createUsersBulk(usersToCreate);
      setCsvUploadResults(result);
      showSuccess(
        `Created ${result.created} of ${usersToCreate.length} account${usersToCreate.length !== 1 ? 's' : ''}.` +
          (result.failed.length > 0 ? ` ${result.failed.length} failed.` : '')
      );

      if (result.created > 0) {
        setUserCsvFile(null);
        setCsvFileAnalysis(null);
        setCsvContentCache(null);
        setPastedCsvText('');
        setPendingAccountCount(null);
        const fileInput = document.getElementById('user-csv-file-upload');
        if (fileInput instanceof HTMLInputElement) {
          fileInput.value = '';
        }
        await fetchUsers(0, [undefined], usersFilter);
      }
    } catch (error) {
      logError(error, 'handleCsvUpload');
      showError(
        getContextualError(
          error,
          undefined,
          'Failed to create accounts from CSV. Please check the file format and try again.'
        )
      );
    } finally {
      setLoading('createUsersBulk', false);
    }
  };

  // Delete — replaces the 'createUserAccounts' action:'delete' httpsCallable.
  const handleDeleteUser = async (user: UserResponse) => {
    if (
      !window.confirm(
        `Delete account for ${user.email}? This action cannot be undone.`
      )
    ) {
      return;
    }

    if (
      !window.confirm(
        `Are you sure? This will permanently delete the account and all associated data for ${user.email}.`
      )
    ) {
      return;
    }

    setLoading('deleteUser', true);
    try {
      await deleteUser(user.id);
      showSuccess(`Deleted account for ${user.email}.`);
      await refreshCurrentPage();
    } catch (error) {
      logError(error, 'deleteStudentAccount');
      showError(
        getContextualError(
          error,
          undefined,
          'Failed to delete account. Please try again.'
        )
      );
    } finally {
      setLoading('deleteUser', false);
    }
  };

  // New: Resend Invite — recovery path for a lost/expired invite now that
  // account creation has no tempPassword fallback. 409s if the user already
  // set a password.
  const handleResendInvite = async (user: UserResponse) => {
    if (!window.confirm(`Resend the invite email to ${user.email}?`)) {
      return;
    }

    setLoading('resendInvite', true);
    try {
      await resendInvite(user.id);
      showSuccess(`Invite email resent to ${user.email}.`);
    } catch (error) {
      logError(error, 'resendInvite');
      showError(
        getContextualError(
          error,
          undefined,
          'Failed to resend invite. Please try again.'
        )
      );
    } finally {
      setLoading('resendInvite', false);
    }
  };

  // New: View PIN — fetches the student's current pinCode (the backend only
  // returns it to an ADMIN+ viewing a student that isn't themselves) and
  // reveals it inline on that row for the parent voice-lookup workflow.
  const handleViewPin = async (user: UserResponse) => {
    setPinLoading(user.id, 'view', true);
    try {
      const result = await getUser(user.id);
      setPinByUserId((prev) => ({ ...prev, [user.id]: result.pinCode ?? null }));
    } catch (error) {
      logError(error, 'viewUserPin');
      showError(
        getContextualError(
          error,
          undefined,
          'Failed to load PIN. Please try again.'
        )
      );
    } finally {
      setPinLoading(user.id, 'view', false);
    }
  };

  // New: Regenerate PIN — mints a fresh PIN for the student (invalidating the
  // old one) and reveals the new value inline. 409s if the target isn't a
  // STUDENT, but the button is only offered on student rows.
  const handleRegeneratePin = async (user: UserResponse) => {
    if (
      !window.confirm(
        `Regenerate the PIN for ${user.email}? Their current PIN will stop working immediately.`
      )
    ) {
      return;
    }

    setPinLoading(user.id, 'regenerate', true);
    try {
      const result = await regeneratePin(user.id);
      setPinByUserId((prev) => ({ ...prev, [user.id]: result.pinCode ?? null }));
      showSuccess(`Regenerated PIN for ${user.email}.`);
    } catch (error) {
      logError(error, 'regenerateUserPin');
      showError(
        getContextualError(
          error,
          undefined,
          'Failed to regenerate PIN. Please try again.'
        )
      );
    } finally {
      setPinLoading(user.id, 'regenerate', false);
    }
  };

  return (
    <CollapsibleSection
      title="User Account Management"
      icon="👥"
      id="user-account-management"
    >
      <div className="section-content">
        <div className="auth-settings-header">
          <p className="text-muted" style={{ marginBottom: '20px' }}>
            Create and manage user accounts. New accounts receive an invite
            email to set their password.
          </p>
        </div>

        <div className={styles['single-user-section']}>
          <h4>👤 Create Single User Account</h4>
          <div className="single-user-form">
            <div className={styles['form-group']}>
              <label htmlFor="single-user-email">Email Address *</label>
              <input
                id="single-user-email"
                type="email"
                value={singleUserEmail}
                onChange={(e) => setSingleUserEmail(e.target.value)}
                placeholder="user@example.com"
                className={styles['form-input']}
                disabled={loadingStates.createUser}
              />
            </div>

            <div className={styles['form-group']}>
              <label htmlFor="single-user-name">Full Name *</label>
              <input
                id="single-user-name"
                type="text"
                value={singleUserName}
                onChange={(e) => setSingleUserName(e.target.value)}
                placeholder="John Smith"
                className={styles['form-input']}
                disabled={loadingStates.createUser}
              />
            </div>

            <div className={styles['form-group']}>
              <label htmlFor="single-user-role">Role *</label>
              <select
                id="single-user-role"
                value={singleUserRole}
                onChange={(e) => setSingleUserRole(e.target.value as Role)}
                className={styles['form-input']}
                disabled={loadingStates.createUser}
              >
                <option value="STUDENT">Student</option>
                <option value="TEACHER">Teacher</option>
                <option value="ADMIN">Admin</option>
              </select>
            </div>

            <button
              className="primary-button"
              onClick={createSingleUserAccount}
              disabled={
                loadingStates.createUser || !singleUserEmail || !singleUserName
              }
            >
              {loadingStates.createUser
                ? 'Creating Account...'
                : 'Create Account'}
            </button>
          </div>
        </div>

        <div className={styles['csv-upload-section']}>
          <CsvFormatExplainer
            title="Bulk Create via CSV"
            summary="Upload or paste CSV data to create many user accounts at once."
            format="email,name,role"
            columns={[
              {
                name: 'email',
                description: "the account's email address (must be unique).",
              },
              { name: 'name', description: "the person's full name." },
              {
                name: 'role',
                description: 'one of student, teacher, or admin.',
              },
            ]}
            example={`email,name,role
student1@school.edu,John Smith,student
teacher1@school.edu,Jane Doe,teacher
admin1@school.edu,Bob Wilson,admin`}
          />

          <div className={styles['form-group']}>
            <label htmlFor="user-csv-file-upload">Choose CSV File:</label>
            <input
              id="user-csv-file-upload"
              type="file"
              accept=".csv"
              className={adminStyles['file-input']}
              disabled={
                loadingStates.createUsersBulk || pastedCsvText.trim().length > 0
              }
              onChange={(e) => handleCsvFileSelection(e.target.files?.[0] ?? null)}
            />
            {userCsvFile && (
              <div className="file-analysis">
                <small className="file-selected">
                  ✅ Selected: {userCsvFile.name} (
                  {csvFileAnalysis?.fileSize ||
                    (userCsvFile.size / 1024).toFixed(1)}{' '}
                  KB)
                </small>
                {csvFileAnalysis && (
                  <div className="csv-analysis">
                    <small className="account-count">
                      📊 Estimated accounts: {csvFileAnalysis.accountCount}
                    </small>
                    {csvFileAnalysis.isLargeFile && (
                      <small className="time-warning">
                        ⚠️ Large file - accounts will be created in batches of
                        100 automatically
                      </small>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="user-csv-paste">Or Paste CSV Data:</label>
            <textarea
              id="user-csv-paste"
              className={styles['csv-textarea']}
              rows={3}
              value={pastedCsvText}
              onChange={(e) => setPastedCsvText(e.target.value)}
              placeholder="email,name,role"
              disabled={loadingStates.createUsersBulk || Boolean(userCsvFile)}
            />
          </div>

          <button
            className="primary-button"
            onClick={handleCsvUpload}
            disabled={
              loadingStates.createUsersBulk ||
              (!userCsvFile && !pastedCsvText.trim())
            }
          >
            {loadingStates.createUsersBulk
              ? `Creating ${pendingAccountCount ?? ''} Accounts...`
              : 'Upload and Create Accounts'}
          </button>

          {csvUploadResults && (
            <div className="csv-upload-results">
              <h4>CSV Upload Results</h4>
              <div className="results-summary">
                <div className="success-count">
                  ✅ Successfully created: {csvUploadResults.created} accounts
                </div>
                {csvUploadResults.failed.length > 0 && (
                  <div className={styles['error-count']}>
                    ❌ Failed: {csvUploadResults.failed.length} accounts
                  </div>
                )}
              </div>

              {csvUploadResults.failed.length > 0 && (
                <div className="failed-accounts">
                  <h5>Failed Rows</h5>
                  <div className="errors-list">
                    {csvUploadResults.failed.map((failure) => (
                      <div key={failure.index} className="error-item">
                        Row {failure.index + 2} (
                        <strong>{failure.email}</strong>
                        ): {failure.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="results-note">
                <p>
                  <strong>Note:</strong> Successfully created accounts have been
                  sent an invite email to set their password.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className={styles['existing-accounts-section']}>
          <h4>Existing Accounts</h4>

          <SearchFilter
            value={usersFilter}
            onChange={setUsersFilter}
            placeholder="Search by name or email..."
            disabled={loadingStates.loadUsers}
          />

          {loadingStates.loadUsers ? (
            <div className="loading">Loading accounts...</div>
          ) : (
            <>
              {users.length === 0 ? (
                <div className="no-results">
                  {usersFilter
                    ? 'No accounts found matching search criteria.'
                    : 'No accounts found. Create one above.'}
                </div>
              ) : (
                <div className="accounts-table-container">
                <table className="accounts-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td className="account-name">{user.name || 'N/A'}</td>
                        <td className="account-email">{user.email}</td>
                        <td className="account-role">
                          {user.role === 'STUDENT' ||
                          user.role === 'SUPER_ADMIN' ? (
                            <span className="role-readonly">
                              {ROLE_LABELS[user.role] || user.role}
                            </span>
                          ) : (
                            <select
                              value={user.role}
                              onChange={(e) =>
                                handleRoleChange(user, e.target.value as Role)
                              }
                              disabled={loadingStates.userRole}
                              className={styles['role-select']}
                            >
                              <option value="TEACHER">Teacher</option>
                              <option value="ADMIN">Admin</option>
                            </select>
                          )}
                        </td>
                        <td className="account-created">
                          {user.createdAt
                            ? new Date(user.createdAt).toLocaleDateString()
                            : 'N/A'}
                        </td>
                        <td className={styles['account-actions']}>
                          {user.role === 'STUDENT' && (
                            <>
                              <button
                                onClick={() => handleViewPin(user)}
                                disabled={Boolean(
                                  pinLoadingByUserId[user.id]?.view
                                )}
                                className={styles['pin-button']}
                                title="View this student's PIN"
                              >
                                🔢 View PIN
                              </button>
                              <button
                                onClick={() => handleRegeneratePin(user)}
                                disabled={Boolean(
                                  pinLoadingByUserId[user.id]?.regenerate
                                )}
                                className={styles['pin-button']}
                                title="Regenerate this student's PIN"
                              >
                                🔄 Regenerate PIN
                              </button>
                              {Object.prototype.hasOwnProperty.call(
                                pinByUserId,
                                user.id
                              ) && (
                                <span
                                  className={styles['pin-display']}
                                  aria-live="polite"
                                >
                                  PIN: {pinByUserId[user.id] ?? 'None'}
                                </span>
                              )}
                            </>
                          )}
                          <button
                            onClick={() => handleResendInvite(user)}
                            disabled={loadingStates.resendInvite}
                            className={styles['invite-btn']}
                            title="Resend invite email"
                          >
                            ✉️ Resend Invite
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user)}
                            disabled={loadingStates.deleteUser}
                            className={styles['btn-delete']}
                            title="Delete account"
                          >
                            🗑️ Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}

              {/* Rendered outside the empty/non-empty split so a
                  SUPER_ADMIN whose per-school client filter emptied the
                  current page still gets Previous/Next when navigation is
                  possible — otherwise they'd be stranded on an empty page
                  with no way back or forward. */}
              {(currentPage > 0 || hasNextPage) && (
                <div className={styles['pagination-controls']}>
                  <div className={styles['pagination-buttons']}>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 0 || loadingStates.loadUsers}
                      className={styles['pagination-btn']}
                      title="Previous page"
                    >
                      Previous
                    </button>
                    <span className={styles['page-info']}>
                      Page {currentPage + 1}
                    </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!hasNextPage || loadingStates.loadUsers}
                      className={styles['pagination-btn']}
                      title="Next page"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="account-management-help">
          <h4>Important Notes:</h4>
          <ul>
            <li>
              <strong>Invite Emails:</strong> New accounts receive an email to
              set their own password. If it's lost or expires, use "Resend
              Invite".
            </li>
            <li>
              <strong>CSV Upload:</strong> Required columns are email,name,role.
            </li>
          </ul>
        </div>
      </div>
    </CollapsibleSection>
  );
};

export default UserManagementPanel;
